import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'
import DoubleScrollContainer from '../../components/DoubleScrollContainer'

interface Property {
    _id: string; title: string; price: number; city?: string; status: string;
    type: string; isActive: boolean; isFeatured: boolean; isApproved: boolean; createdAt: string;
    owner?: { name: string; email: string; role: string };
}

export default function AdminAllProperties() {
    const { token } = useAuth()
    const [properties, setProperties] = useState<Property[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState<string | null>(null)
    const [togglingFeatured, setTogglingFeatured] = useState<string | null>(null)
    const [togglingApproved, setTogglingApproved] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [search, setSearch] = useState('')

    useEffect(() => {
        if (!token) { setLoading(false); return }
        api.adminGetAllProperties(token)
            .then(data => { if (Array.isArray(data)) setProperties(data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [token])

    const handleDelete = async (id: string, title: string) => {
        if (!token || !window.confirm(`Delete property "${title}"?`)) return
        setDeleting(id)
        const res = await api.adminDeleteProperty(token, id)
        if (res.message === 'Property deleted successfully') {
            setProperties(prev => prev.filter(p => p._id !== id))
            setMsg({ type: 'success', text: `Property deleted.` })
        } else {
            setMsg({ type: 'error', text: res.message || 'Delete failed.' })
        }
        setDeleting(null)
    }

    const handleToggleFeatured = async (id: string, currentStatus: boolean) => {
        if (!token) return;
        setTogglingFeatured(id);
        const res = await api.adminToggleFeatured(token, id, !currentStatus);
        if (res._id) {
            setProperties(prev => prev.map(p => p._id === id ? { ...p, isFeatured: !currentStatus } : p));
        } else {
            setMsg({ type: 'error', text: res.message || 'Failed to update featured status.' });
        }
        setTogglingFeatured(null);
    }

    const handleToggleApproved = async (id: string, currentStatus: boolean) => {
        if (!token) return;
        setTogglingApproved(id);
        const res = await api.adminApproveProperty(token, id, !currentStatus);
        if (res._id) {
            setProperties(prev => prev.map(p => p._id === id ? { ...p, isApproved: !currentStatus } : p));
            setMsg({ type: 'success', text: `Property ${!currentStatus ? 'approved' : 'disapproved'} successfully.` });
        } else {
            setMsg({ type: 'error', text: res.message || 'Failed to update approval status.' });
        }
        setTogglingApproved(null);
    }

    const filtered = properties.filter(p =>
        (p.title || '').includes(search) ||
        (p.city || '').includes(search) ||
        (p.owner?.name || '').includes(search)
    )

    const statusColor = (s: string) => s === 'For Rent' ? '#3b82f6' : '#10b981'

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-black mb-2 leading-none">Property Inventory</h1>
                    <p className="text-black font-bold text-[14px] md:text-[16px]">Manage and monitor every property listed on the platform.</p>
                </div>
                <Link to="/admin-add-property" className="px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md text-[12px] md:text-[14px] font-bold transition-all active:scale-95 flex items-center justify-center gap-3 shadow-md">
                    <i className="fa-solid fa-plus text-xs"></i> Add property
                </Link>
            </div>

            {msg && (
                <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} border-0 shadow-sm mb-4`}>
                    {msg.text}
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-xl overflow-hidden mb-20">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6 bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] flex items-center justify-center shadow-xl shadow-blue-100">
                            <i className="fa-solid fa-building-circle-check text-xl"></i>
                        </div>
                        <div>
                            <h3 className="text-xl md:text-2xl font-bold text-black dark:text-white leading-none">Global Inventory</h3>
                            <p className="text-[13px] md:text-[14px] font-bold text-black mt-1.5">{properties.length} Active nodes identified</p>
                        </div>
                    </div>
                    
                    <div className="relative group w-full md:w-[350px]">
                        <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-blue-900 group-focus-within:text-blue-950 transition-colors text-xs"></i>
                        <input 
                            type="text" 
                            className="w-full bg-white dark:bg-slate-800 border-none rounded-md py-4 pl-14 pr-6 text-[14px] md:text-[15px] font-bold text-black focus:ring-2 focus:ring-[#05144b] transition-all shadow-inner placeholder:text-gray-400"
                            placeholder="Search properties..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="p-32 text-center">
                        <div className="w-16 h-16 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                        <p className="text-blue-900 font-black uppercase tracking-widest text-[10px] italic">Syncing inventory...</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-32 text-center">
                        <div className="w-24 h-24 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-8">
                            <i className="fa-solid fa-building-circle-exclamation text-4xl text-blue-900"></i>
                        </div>
                        <h3 className="text-xl font-black text-blue-950 dark:text-white mb-2 italic uppercase">{search ? 'No matches found' : 'No properties yet'}</h3>
                        <p className="text-blue-900 text-[10px] font-black uppercase tracking-widest italic max-w-xs mx-auto">Adjust your search parameters or register new inventory.</p>
                    </div>
                ) : (
                    <DoubleScrollContainer>
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-white/50">
                                <tr>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Property Identity</th>
                                    <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Evaluation</th>
                                    <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Visibility</th>
                                    <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Protocol</th>
                                    <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Status</th>
                                    <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Location</th>
                                    <th className="px-6 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Ownership</th>
                                    <th className="px-8 py-5 text-right text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100/50">
                                {filtered.map(p => (
                                    <tr key={p._id} className="group hover:bg-white/50 transition-all">
                                        <td className="px-8 py-6">
                                            <div className="text-[15px] md:text-[16px] font-bold text-black dark:text-white transition-transform">{p.title}</div>
                                            <div className="text-[12px] font-medium text-black/60 mt-1">{p.type}</div>
                                        </td>
                                        <td className="px-6 py-6 font-bold text-black tabular-nums text-[15px] md:text-[17px]">
                                            ₹{p.price?.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-6">
                                            <button
                                                className="w-10 h-10 rounded-md border border-blue-900/10 flex items-center justify-center hover:bg-amber-50 transition-colors shadow-sm disabled:opacity-50"
                                                onClick={() => handleToggleFeatured(p._id, p.isFeatured)}
                                                disabled={togglingFeatured === p._id}
                                            >
                                                {togglingFeatured === p._id ? (
                                                    <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <i className={`fa-${p.isFeatured ? 'solid text-amber-500' : 'regular text-blue-100'} fa-star text-sm`}></i>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-6 py-6">
                                            <button
                                                className="w-10 h-10 rounded-md border border-blue-900/10 flex items-center justify-center hover:bg-blue-50 transition-colors shadow-sm disabled:opacity-50"
                                                onClick={() => handleToggleApproved(p._id, p.isApproved)}
                                                disabled={togglingApproved === p._id}
                                            >
                                                {togglingApproved === p._id ? (
                                                    <div className="w-4 h-4 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <i className={`fa-solid ${p.isApproved ? 'fa-circle-check text-blue-600' : 'fa-circle-xmark text-slate-200'} text-sm`}></i>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-6 py-6">
                                            <span className="text-[12px] font-bold text-black bg-gray-50 px-2.5 py-1.5 rounded-md border border-gray-100">
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-[12px] font-medium text-black/60 whitespace-nowrap">
                                            <i className="fa-solid fa-location-dot mr-1.5"></i>{p.city || '—'}
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="text-[13px] font-bold text-black">{p.owner?.name || '—'}</div>
                                            <div className={`text-[11px] font-medium mt-1 ${p.owner?.role === 'agent' ? 'text-emerald-600' : 'text-blue-600'}`}>
                                                {p.owner?.role} profile
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Link to={`/property-details/${p._id}`} className="w-9 h-9 rounded-md border border-blue-900/10 bg-white flex items-center justify-center text-blue-950 hover:bg-blue-950 hover:text-white transition-all shadow-sm">
                                                    <i className="fa-solid fa-eye text-xs"></i>
                                                </Link>
                                                <Link to={`/admin-edit-property/${p._id}`} className="w-9 h-9 rounded-md border border-blue-900/10 bg-white flex items-center justify-center text-blue-950 hover:bg-blue-950 hover:text-white transition-all shadow-sm">
                                                    <i className="fa-solid fa-pen-to-square text-xs"></i>
                                                </Link>
                                                <button className="w-9 h-9 rounded-md border border-blue-900/10 bg-white flex items-center justify-center text-rose-600 hover:bg-rose-600 hover:text-white transition-all shadow-sm disabled:opacity-50"
                                                    disabled={deleting === p._id}
                                                    onClick={() => handleDelete(p._id, p.title)}
                                                >
                                                    {deleting === p._id
                                                        ? <div className="w-3 h-3 border-2 border-rose-600 border-t-transparent rounded-full animate-spin"></div>
                                                        : <i className="fa-solid fa-trash-can text-xs"></i>
                                                    }
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </DoubleScrollContainer>
                )}
            </div>
        </AdminLayout>
    )
}


